(() => {
  if (customElements.get('product-variant-selector')) return;

  class ProductVariantSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.closest('form[data-productform="true"], form[data-product-form="true"]');
      this.infoRoot = this.closest('.product__info-wrapper, .quickshop__info-wrapper');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.message = this.querySelector('[data-variant-message]');
      this.groups = Array.from(this.querySelectorAll('[data-option-group]'));
      this.variants = this.parseJson('[data-variant-data]', []);
      this.swatches = this.parseJson('[data-swatch-data]', {});

      if (!this.form || !this.variantInput || !this.variants.length) return;
      this.dataset.controllerReady = 'true';

      this.decorateSwatches();
      this.addEventListener('change', this.handleChange.bind(this));
      this.refreshOptionStates();
      this.updateVariant(this.findSelectedVariant(), false);
    }

    parseJson(selector, fallback) {
      const element = this.querySelector(selector);
      if (!element) return fallback;

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.warn('Variant selector data could not be parsed.', error);
        return fallback;
      }
    }

    handleChange(event) {
      const input = event.target.closest('[data-option-input]');
      if (!input) return;

      const changedPosition = Number(input.dataset.optionPosition);
      this.clearInvalidDownstreamSelections(changedPosition);
      this.refreshOptionStates();
      this.updateVariant(this.findSelectedVariant(), true);
    }

    selectedOptions() {
      return this.groups.map((group) => {
        const checked = group.querySelector('[data-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    findSelectedVariant() {
      const selected = this.selectedOptions();
      if (selected.some((value) => value === null)) return null;

      return this.variants.find((variant) =>
        variant.options.every((value, index) => value === selected[index])
      ) || null;
    }

    clearInvalidDownstreamSelections(changedPosition) {
      for (let index = changedPosition; index < this.groups.length; index += 1) {
        const checked = this.groups[index].querySelector('[data-option-input]:checked');
        if (!checked) continue;

        const selections = this.selectedOptions();
        const prefixExists = this.variants.some((variant) =>
          selections.slice(0, index + 1).every((value, optionIndex) =>
            value === null || variant.options[optionIndex] === value
          )
        );

        if (!prefixExists) checked.checked = false;
      }
    }

    refreshOptionStates() {
      const selected = this.selectedOptions();

      this.groups.forEach((group, index) => {
        group.querySelectorAll('[data-option-input]').forEach((input) => {
          const candidates = this.variants.filter((variant) => {
            const precedingOptionsMatch = selected.slice(0, index).every((value, optionIndex) =>
              value === null || variant.options[optionIndex] === value
            );
            return precedingOptionsMatch && variant.options[index] === input.value;
          });
          const exists = candidates.length > 0;
          const soldOut = exists && !candidates.some((variant) => variant.available);
          const tile = input.closest('[data-option-tile]');
          const status = tile ? tile.querySelector('[data-option-status]') : null;

          input.disabled = !exists;
          if (tile) {
            tile.classList.toggle('is-unavailable', !exists);
            tile.classList.toggle('is-sold-out', soldOut);
          }
          if (status) {
            status.textContent = !exists
              ? this.dataset.unavailableLabel
              : soldOut
                ? this.dataset.soldOutLabel
                : '';
          }
        });
      });
    }

    updateVariant(variant, updateUrl) {
      this.variantInput.value = variant ? variant.id : '';
      this.dataset.selectedVariant = variant ? variant.id : '';
      this.toggleMessage(!variant);
      this.updateProductInformation(variant);
      this.updateButtons(variant);

      if (!variant) return;
      if (updateUrl && this.dataset.context === 'product') this.updateUrl(variant.id);
      this.dispatchVariantChange(variant);
    }

    toggleMessage(show) {
      if (!this.message) return;
      this.message.hidden = !show;
      this.message.textContent = show ? this.dataset.selectOptionsLabel : '';
    }

    updateProductInformation(variant) {
      if (!variant || !this.infoRoot) return;

      const price = this.infoRoot.querySelector('[data-price]');
      const priceContainer = this.infoRoot.querySelector(
        '.product__price-container, .quickshop__price-container'
      );
      let compareAtPrice = this.infoRoot.querySelector('[data-compare-at-price]');
      const onSale = variant.compareAtPrice && variant.compareAtPrice > variant.price;

      if (price) price.textContent = variant.priceMoney;

      if (onSale && priceContainer) {
        if (!compareAtPrice) {
          compareAtPrice = document.createElement('span');
          compareAtPrice.className = `${this.dataset.context}__compare-at-price`;
          compareAtPrice.dataset.compareAtPrice = '';
          compareAtPrice.appendChild(document.createElement('span'));
          priceContainer.prepend(compareAtPrice);
        }
        compareAtPrice.querySelector('span').textContent = variant.compareAtPriceMoney;
      } else if (compareAtPrice) {
        compareAtPrice.remove();
      }

      const currentPrice = price ? price.closest('.product__price, .quickshop__price') : null;
      if (currentPrice) {
        currentPrice.classList.toggle(`${this.dataset.context}__price__sale`, Boolean(onSale));
      }

      const skuWrapper = this.infoRoot.querySelector('[data-product-sku]');
      const sku = this.infoRoot.querySelector('[data-sku-value]');
      if (sku) sku.textContent = variant.sku || '';
      if (skuWrapper) skuWrapper.hidden = !variant.sku;

      this.updateQuantityLimits(variant);
      const notifySelect = this.infoRoot.querySelector('[data-notify-variant-select]');
      if (notifySelect && !variant.available) notifySelect.value = String(variant.id);
    }

    updateQuantityLimits(variant) {
      const container = this.closest('.product__info, .quickshop__info');
      if (!container) return;

      container.querySelectorAll('.item-quantity').forEach((input) => {
        const productMax = Number(input.dataset.productMax) || 100;
        const inventoryMax = Number(variant.inventoryQuantity);
        const max = inventoryMax > 0 ? Math.min(productMax, inventoryMax) : productMax;
        input.max = String(max);
        if (Number(input.value) > max) input.value = String(max);
      });
    }

    updateButtons(variant) {
      const primary = this.form.querySelector('[data-variant-atc]');
      const unavailable = this.form.querySelector('[data-variant-unavailable]');
      const sticky = this.closest('.product__info')?.querySelector('[data-sticky-atc-button]');
      const complete = Boolean(variant);
      const available = Boolean(variant && variant.available);
      const hulkLoading = primary?.dataset.hulkLoading === 'true';

      if (primary) {
        primary.hidden = complete && !available;
        primary.disabled = !available || primary.dataset.preorder === 'true' || hulkLoading;
      }
      if (unavailable) unavailable.hidden = !complete || available;

      if (sticky) {
        const source = available ? primary : unavailable;
        sticky.disabled = !complete || (available && primary?.disabled);
        sticky.dataset.stickySubmitTarget = available ? 'atc' : 'alternative';
        if (source) sticky.querySelector('span').textContent = source.querySelector('span').textContent;
      }
    }

    updateUrl(variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }

    dispatchVariantChange(variant) {
      const selected = this.selectedOptions();
      const selectedOptions = selected.reduce((options, value, index) => {
        options[`option${index + 1}`] = value;
        return options;
      }, {});

      document.dispatchEvent(new CustomEvent('variantChanged', {
        detail: {
          wrapper: this,
          form: this.form,
          productId: String(this.dataset.productId),
          context: this.dataset.context,
          variant: String(variant.id),
          variantCompareAtPriceMoneyString: variant.compareAtPriceMoney || null,
          variantPriceMoneyString: variant.priceMoney,
          variantInventoryQuantity: variant.inventoryQuantity,
          variantSelectedOptions: selectedOptions,
          available: variant.available,
          media: variant.featuredMediaUrl,
          variantMediaPosition: variant.featuredMediaPosition
        }
      }));
      document.dispatchEvent(new CustomEvent('shopify-event-variant-changed', {
        detail: { variantId: String(variant.id), productId: String(this.dataset.productId) }
      }));
    }

    decorateSwatches() {
      this.querySelectorAll('[data-swatch-value]').forEach((swatch) => {
        const value = swatch.dataset.swatchValue;
        const className = value.replace(/\d/g, '').trim().toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
        const background = this.swatches?.[value] || window.ThemeVars?.swatches?.[value];

        if (className) swatch.classList.add(className);
        if (!background) return;

        if (/^(https?:|\/\/|url\()/i.test(background)) {
          swatch.style.backgroundImage = background.startsWith('url(')
            ? background
            : `url("${background}")`;
        } else {
          swatch.style.backgroundColor = background;
        }
      });
    }
  }

  customElements.define('product-variant-selector', ProductVariantSelector);
})();
